import React, { useState } from 'react';
import UserSelect from './UserSelect';

/**
 * List + create form for one person's granted delegations (issue #44).
 *
 * Presentational: the parent owns fetching and the API calls, so the same
 * component serves both surfaces without knowing which it is on --
 * ProfilePage's "Delegates I've granted" (self-service, POST
 * /api/auth/me/delegations) and DelegationsModal's admin equivalent (POST
 * /api/users/{id}/delegations). Those two endpoints are gated very
 * differently, which is exactly why that decision does not belong in here.
 *
 * Revoked rows stay listed rather than disappearing: "I did switch that off
 * before I went away" is what this list is usually opened to check, and hiding
 * them makes an absent row ambiguous between "never existed" and "revoked".
 */
function DelegationsPanel({
  delegations,
  onCreate,
  onRevoke,
  busy = false,
  disabled = false,
  subjectLabel = 'your',
}) {
  const [delegateId, setDelegateId] = useState(null);
  const [canApprovals, setCanApprovals] = useState(true);
  const [canDocuments, setCanDocuments] = useState(false);
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!delegateId) return;
    onCreate({
      delegate_user_id: delegateId,
      can_act_on_approvals: canApprovals,
      can_edit_owned_documents: canDocuments,
      // Empty string means "unset", not "epoch": a delegation with no start is
      // in force now and one with no end runs until revoked.
      starts_at: startsAt ? new Date(startsAt).toISOString() : null,
      ends_at: endsAt ? new Date(endsAt).toISOString() : null,
      reason: reason.trim() || null,
    });
    setDelegateId(null);
    setStartsAt('');
    setEndsAt('');
    setReason('');
  };

  const describe = (d) => [
    d.can_act_on_approvals && 'Approvals',
    d.can_edit_owned_documents && 'Owned documents',
  ].filter(Boolean).join(', ');

  const windowLabel = (d) => {
    const fmt = (v) => new Date(v).toLocaleDateString();
    if (d.starts_at && d.ends_at) return `${fmt(d.starts_at)} – ${fmt(d.ends_at)}`;
    if (d.ends_at) return `Until ${fmt(d.ends_at)}`;
    if (d.starts_at) return `From ${fmt(d.starts_at)}`;
    return 'Until revoked';
  };

  return (
    <div>
      {delegations.length === 0 ? (
        <div className="empty-state" style={{ padding: '12px 0' }}>
          No delegates yet.
        </div>
      ) : (
        <div className="table-container" style={{ marginBottom: '16px' }}>
        <table>
          <thead>
            <tr>
              <th>Delegate</th>
              <th>Can act on</th>
              <th>When</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {delegations.map((d) => (
              <tr key={d.id}>
                <td>{d.delegate_name}</td>
                <td>{describe(d)}</td>
                <td>{windowLabel(d)}</td>
                <td>
                  {/* is_live is computed server-side: revocation and either
                      party's deactivation feed into it as well as the dates,
                      so it must not be re-derived from the window here. */}
                  <span className={`badge ${d.is_live ? 'badge-active' : 'badge-draft'}`}>
                    {d.revoked_at ? 'Revoked' : d.is_live ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td>
                  {!d.revoked_at && (
                    <button
                      className="btn btn-danger btn-small"
                      onClick={() => onRevoke(d)}
                      disabled={busy || disabled}
                    >
                      Revoke
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Delegate</label>
          <UserSelect
            value={delegateId}
            onChange={setDelegateId}
            placeholder="Choose a person"
            disabled={disabled}
          />
        </div>
        <div className="form-group">
          <label>They may act on {subjectLabel} behalf for</label>
          <label style={{ display: 'block', fontWeight: 'normal' }}>
            <input
              type="checkbox"
              checked={canApprovals}
              onChange={(e) => setCanApprovals(e.target.checked)}
              disabled={disabled}
            />{' '}
            Approvals — gates held by committees {subjectLabel === 'your' ? 'you sit' : 'they sit'} on
          </label>
          <label style={{ display: 'block', fontWeight: 'normal' }}>
            <input
              type="checkbox"
              checked={canDocuments}
              onChange={(e) => setCanDocuments(e.target.checked)}
              disabled={disabled}
            />{' '}
            Documents {subjectLabel === 'your' ? 'you own' : 'they own'}
          </label>
        </div>
        {/* One window, so one line -- see .form-row in global.css. */}
        <div className="form-row">
          <div className="form-group">
            <label>From (optional)</label>
            <input
              type="date"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              disabled={disabled}
            />
          </div>
          <div className="form-group">
            <label>Until (optional)</label>
            <input
              type="date"
              value={endsAt}
              onChange={(e) => setEndsAt(e.target.value)}
              disabled={disabled}
            />
          </div>
        </div>
        <div className="form-group">
          <label>Reason (optional)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Vacation 1–14 Aug"
            disabled={disabled}
          />
        </div>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={busy || disabled || !delegateId || (!canApprovals && !canDocuments)}
        >
          {busy ? 'Saving...' : 'Add delegate'}
        </button>
      </form>
    </div>
  );
}

export default DelegationsPanel;
